import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import db, { collection, orderBy, query } from '../firebase/firestore';
import { subscribeCollection } from '../hooks/useResilientSnapshot';
import { LEAD_STAGES } from '../utils/leadStages';
import { STAGES } from '../utils/stages';

const StagesContext = createContext(null);

function useCustomStages(academyId, subcollection) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!academyId) { setLoading(false); return; }
    const q = query(collection(db, 'academies', academyId, subcollection), orderBy('order', 'asc'));
    return subscribeCollection(q, setStages, setLoading);
  }, [academyId, subcollection]);

  return { stages, loading };
}

function mergeStages(systemStages, customStages, overridesByKey) {
  return [...systemStages, ...customStages.map((s) => ({ ...s, isSystem: false }))]
    .map((s) => {
      const label = overridesByKey.get(s.key)?.label;
      return label ? { ...s, label } : s;
    })
    .sort((a, b) => a.order - b.order);
}

// Custom stages (added by an academy admin in Settings) merge in alongside
// the fixed system stages — see utils/leadStages.js / utils/stages.js for
// why those can't just be replaced outright. Anything that renders the full
// pipeline (Kanban columns, stage pickers, badges) should read from here
// rather than importing LEAD_STAGES/STAGES directly, so a custom stage
// shows up everywhere a system one does.
export function StagesProvider({ children }) {
  const { academyId } = useAuth();
  const { stages: customLeadStages, loading: leadLoading } = useCustomStages(academyId, 'customLeadStages');
  const { stages: customPlayerStages, loading: playerLoading } = useCustomStages(academyId, 'customPlayerStages');
  // Per-academy overrides on a system stage — hidden (see
  // deleteSystemStage/restoreSystemStage) and/or relabelled (e.g. a
  // PLYRMATCH partner's 'awaitingAcademyContract' reading "Contract Sent" —
  // see createPartnerAcademy). One flat collection covering both lead and
  // player stage keys, since the two never collide.
  const { stages: hiddenStages, loading: hiddenLoading } = useCustomStages(academyId, 'hiddenStages');
  // Agency-only free-form tag on a lead/player (see createSubCategory) —
  // empty for every non-agency tenant, harmless to always subscribe.
  const { stages: subCategories, loading: subCategoriesLoading } = useCustomStages(academyId, 'subCategories');

  const value = useMemo(() => {
    const overridesByKey = new Map(hiddenStages.map((s) => [s.key, s]));
    // Missing `hidden` only happens on a doc written before that field
    // existed — treat as hidden, matching what deleteSystemStage meant by
    // "this doc exists" before the field was added.
    const isHidden = (s) => s.hidden !== false;
    const hiddenLeadKeys = new Set(hiddenStages.filter((s) => s.stageType === 'lead' && isHidden(s)).map((s) => s.key));
    const hiddenPlayerKeys = new Set(hiddenStages.filter((s) => s.stageType === 'player' && isHidden(s)).map((s) => s.key));

    // Unfiltered — every system + custom stage (relabelled where
    // overridden), regardless of hidden status. Settings needs this to
    // offer hiding/restoring; everything else (badge label lookups) uses it
    // too, so a stray record sitting in a hidden stage still renders with a
    // real label instead of falling back to its raw key.
    const allLeadStages = mergeStages(LEAD_STAGES, customLeadStages, overridesByKey);
    const allPlayerStages = mergeStages(STAGES, customPlayerStages, overridesByKey);

    // Filtered — what Kanban columns and the manual stage-picker actually
    // show. A stage can only end up hidden if nothing was on it at the time
    // (see deleteSystemStage/deleteCustomStage), so this shouldn't ever
    // hide a card out of view; the map above is the safety net regardless.
    const leadStages = allLeadStages.filter((s) => !hiddenLeadKeys.has(s.key));
    const playerStages = allPlayerStages.filter((s) => !hiddenPlayerKeys.has(s.key));

    return {
      leadStages,
      playerStages,
      allLeadStages,
      allPlayerStages,
      hiddenLeadKeys,
      hiddenPlayerKeys,
      hiddenStages,
      activeLeadStages: leadStages.filter((s) => s.key !== 'lost'),
      leadStageMap: Object.fromEntries(allLeadStages.map((s) => [s.key, s])),
      playerStageMap: Object.fromEntries(allPlayerStages.map((s) => [s.key, s])),
      customLeadStages,
      customPlayerStages,
      subCategories,
      subCategoryMap: Object.fromEntries(subCategories.map((c) => [c.key, c])),
      loading: leadLoading || playerLoading || hiddenLoading || subCategoriesLoading,
    };
  }, [customLeadStages, customPlayerStages, hiddenStages, subCategories, leadLoading, playerLoading, hiddenLoading, subCategoriesLoading]);

  return <StagesContext.Provider value={value}>{children}</StagesContext.Provider>;
}

export function useStages() {
  const ctx = useContext(StagesContext);
  if (!ctx) throw new Error('useStages must be used within StagesProvider');
  return ctx;
}
